import ArticleContainer from '@bridebook/web/app-basic/components/articles/container/container';
import { FloatingSignUpButton } from '@bridebook/web/app-basic/components/articles/floating-sign-up-button/floating-sign-up-button';
import SearchBar from '@bridebook/web/app-shared/components/articles/search-bar/search-bar';
import Sidebar from '@bridebook/web/app-basic/components/articles/sidebar/sidebar';
import SignUpBanner from '@bridebook/web/app-basic/components/articles/sign-up-banner/sign-up-banner';
import { Breadcrumbs } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs-article.container';
import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import { clickedExternalLinkWithinArticle } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/clicked-external-link-within-article';
import { readArticleOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/read-article-logged-out';
import { getIsMobile, getPreviousPath } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { StickyContainer } from 'react-sticky';
import { mergeClean } from '@bridebook/toolbox/src';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { Box } from '@bridebook/ui';
import {
  TExternalLinkClickCallback,
  renderHtml,
} from '@bridebook/web/app-shared/components/articles/render-html';
import { useRecentArticlesStorage } from '@bridebook/web/app-shared/lib/articles/hooks/use-recent-articles-storage';
import { articleSchema } from '@bridebook/web/app-shared/lib/articles/schema';
import { ArticleBase } from '@bridebook/web/app-shared/lib/articles/schemas/article';
import { IRelatedTaskData } from '@bridebook/web/app-shared/lib/articles/types';
import {
  getArticlePropertiesGeneric,
  getArticleShareImage,
} from '@bridebook/web/app-shared/lib/articles/utils';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import ErrorPage from '@bridebook/web/pages/_error';

export interface IArticlePageProps {
  params: boolean;
  relatedSuppliers: Array<IUISupplier>;
  relatedTaskData: IRelatedTaskData;
  error: boolean;
  locale: string;
  notFound?: boolean;
}

const ArticlePage = ({ error, locale, notFound }: IArticlePageProps) => {
  const { t } = useTranslation('articles');
  const market = useMarket();
  const article = useSelector((state) => state.articles.article);
  const isMobile = useSelector(getIsMobile);
  const dispatch = useDispatch();
  const { ID: articleId } = article || {};
  const recentArticles = useRecentArticlesStorage();
  const isUK = locale === 'uk';
  const [showSignUpFloatingButton, setShowSignUpFloatingButton] = useState(true);
  const { query } = useRouter();
  const { source } = query;
  const previousPath = useSelector(getPreviousPath);

  useEffect(() => {
    if (article) {
      dispatch(
        readArticleOnLoggedOutPage({
          ...getArticlePropertiesGeneric({ article }),
          source: source?.toString(),
          readArticleMethod: source ?? 'article',
          previousPath,
        }),
      );
    }
  }, [article, dispatch, previousPath, source]);

  const handleExternalLinkClick: TExternalLinkClickCallback = useCallback(
    (params) => {
      dispatch(clickedExternalLinkWithinArticle({ ...params, article }));
    },
    [dispatch, article],
  );

  useEffect(
    () => () => {
      window.scroll({
        top: 0,
        left: 0,
      });
    },
    [],
  );

  useEffect(() => {
    if (articleId) {
      recentArticles.add(mergeClean(ArticleBase, article));
    }
  }, [article, articleId, recentArticles]);

  const handleScroll = useCallback(() => {
    const bottom =
      Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight;

    if (bottom) {
      setShowSignUpFloatingButton(false);
    } else {
      setShowSignUpFloatingButton(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (!article?.ID || error || notFound) {
    return <ErrorPage statusCode={404} />;
  }

  const htmlContent = article?.content || '';
  const renderedContent = renderHtml({
    htmlContent,
    market,
    externalLinkClickCallback: handleExternalLinkClick,
    enableTransforms: true,
    styleTable: true,
  });
  const pinImage = getArticleShareImage(htmlContent);

  return (
    <>
      <Head
        isArticle
        plainOgTitle
        title={`${article?.seo_title || article.title} | ${t('weddingAdvice')}`}
        description={article.seo_metadesc || ''}
        ogTitle={article?.seo_title || article.title}
        ogDescription={article.subtitle || ''}
        ogType="article"
        ogImage={pinImage || null}>
        <meta property="article:author" content="Bridebook" />
        <meta property="og:image:width" content="530" />
        <meta property="og:image:height" content="280" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(articleSchema(article, market), null, 2),
          }}
        />
      </Head>
      {isMobile && <SignUpBanner />}
      <TopBar />
      <Box data-name="article-page-wrapper">
        <StickyContainer>
          {!isMobile && (
            <Box ml={5}>
              <Breadcrumbs showAlways />
            </Box>
          )}
          {isMobile && (
            <Box mx={6} mt={6}>
              <SearchBar />
            </Box>
          )}
          <ArticleContainer renderedContent={renderedContent} pinImage={pinImage} />
          {isMobile && <Sidebar />}
        </StickyContainer>
      </Box>
      <Footer />
      {isUK && showSignUpFloatingButton && (
        <FloatingSignUpButton
          label={t('signUpForFree')}
          query="?utm_source=bridebookbanner&utm_medium=floatingcta&utm_campaign=articlefloatingcta"
        />
      )}
    </>
  );
};

export default ArticlePage;
